import {
   Alert,
   Button,
   Card,
   CardData,
   customToast,
   FormInput,
   Icon,
   PostcodeLookup,
   replaceKeyWithValue,
   route,
   SelectionCardsGroup,
   SelectOption,
   Stepper,
   StringHelpers,
   useDisabledContext,
   useLangContext,
   Variants,
} from 'carrier-fe';
import { useMemo, useState } from 'react';
import { StepProps } from '../../../types/RegistrationJourney';
import { SYSTEM_TYPES, SYSTEM_TYPES_OPTIONS } from '../../../util/Constants';
import { ApplianceDetails } from '../../../types/Installation';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { STEPS } from '../../../Constants/InstallationJourney';

function Step2({ form, pageLang, loading }: StepProps) {
   const params = useParams();
   const [searchParams] = useSearchParams();
   const installationId = params?.installationId || searchParams.get('installationId');
   
   const [selects, setSelects] = useState<{ [key: string]: SelectOption[] }>({
      system_types: SYSTEM_TYPES_OPTIONS,
   });
   const [isFetchingAppliance, setIsFetchingAppliance] = useState(false);
   const [applianceLookupData, setApplianceLookupData] = useState<ApplianceDetails | null>(form?.store?.appliances?.[0]?.appliance_details || null);
   const { crud, fields } = useLangContext();
   const { disabled, setDisabled } = useDisabledContext();

   const gasSafeDisclaimer = useMemo(() => {
      const gasSafeDisclaimer = pageLang?.page?.helper_texts?.gas_safe_disclaimer;
      if (form?.store?.system_type === SYSTEM_TYPES.GAS_BOILER) {
         return gasSafeDisclaimer;
      }
      return undefined;
   }, [form?.store?.system_type, pageLang]);

   const findAppliance = async () => {
      setIsFetchingAppliance(true);
      setDisabled(true);
      try {
         const response = await axios.get<{ data: ApplianceDetails; message: string }>(
            route(
               `${'/v1/householder/lookup/serial-number'}?serial_number=${
                  form?.store?.serial_number
               }&completion_date=${
                  form?.store?.completion_date
               }&installation_id=${installationId}&system_type=${form?.store?.system_type}`
            )
         );

         setApplianceLookupData(response?.data?.data);

         customToast({
            title: response?.data?.message,
            variant: Variants.Success,
         });
         return;
      } catch (error: any) {
         customToast({
            title: error?.response?.data?.message,
            variant: Variants.Danger,
         });
      } finally {
         setIsFetchingAppliance(false);
         setDisabled(false);
      }
   };

   const appliance = useMemo(()=> {
      return applianceLookupData ?? form?.store?.appliances?.[0]?.appliance_details

   },[applianceLookupData, form?.store?.appliances])

   

   return (
      <Stepper.Step
         loading={loading}
         description={pageLang?.page?.steps?.[STEPS.STEP1 - 1]?.description || ''}
         title={pageLang?.page?.steps?.[STEPS.STEP1 - 1]?.title || 'System Details'}
      >
         <>
            <SelectionCardsGroup
               options={selects?.system_types as any}
               value={form?.store?.system_type}
               onChange={(value) => {
                  form?.setValues({ system_type: value as any });
                  form?.setErrors({ system_type: undefined });
               }}
               errors={form?.errors?.system_type}
               label={
                  replaceKeyWithValue(
                     crud?.sub_titles?.select || 'Please select a :field',
                     'field',
                     fields?.system_type || 'System Type'
                  ) + ':'
               }
            />
            {gasSafeDisclaimer && (
               <Alert variant={Variants.Warning} className={'d-flex align-items-center'}>
                  <Icon icon="warning" style={{ marginRight: 8 }} />
                  {gasSafeDisclaimer}
               </Alert>
            )}

            {form?.store?.system_type && (
               <>
                  <FormInput
                     disabled={disabled}
                     name="completion_date"
                     type="date"
                     label={StringHelpers.title(fields?.completion_date || 'Completion Date')}
                     value={form?.store?.completion_date}
                     onChange={(value) => {
                        form?.setValues({ completion_date: value as string });
                        form?.setErrors({ completion_date: undefined });
                     }}
                     errorMessages={form?.errors?.completion_date}
                  />

                  <div className="mb-4">
                     <div className="d-flex align-items-start gap-2 justify-content-between">
                        <div className="flex-fill">
                           <FormInput
                              noMargin
                              name={`serial_number`}
                              label={StringHelpers.title(fields?.serial_number || 'Serial Number')}
                              type="text"
                              value={form?.store?.serial_number || ''}
                              onChange={(value) => {
                                 form?.setValues({
                                    serial_number: value as string,
                                 });
                                 
                              }}
                              errorMessages={form?.errors?.serial_number}
                              disabled={disabled}
                           />
                        </div>
                        <Button
                           disabled={!form?.store?.serial_number || disabled}
                           variant={Variants.Dark}
                           type="SOLID"
                           label={StringHelpers.title(
                              isFetchingAppliance
                                 ? crud?.buttons?.find?.submitting || 'Searching... '
                                 : replaceKeyWithValue(
                                      crud?.buttons?.confirm?.dynamic?.default || 'Confirm :any',
                                      'any',
                                      crud?.models?.serial_number || 'serial number'
                                   )
                           )}
                           size="Medium"
                           onClick={() => findAppliance()}
                        />
                     </div>
                     {appliance && (
                        <Card className="p-3 mt-2" noMargin>
                           <div className="d-flex justify-content-between">
                              <CardData.ColumnItem
                                 title={pageLang?.generic?.appliance_details_full_name || ''}
                                 value={appliance?.full_name}
                              />
                              <CardData.ColumnItem
                                 title={
                                    pageLang?.generic?.appliance_details_installation_type || ''
                                 }
                                 value={appliance?.installation_type}
                              />
                              <CardData.ColumnItem
                                 title={pageLang?.generic?.appliance_model || ''}
                                 value={appliance?.model}
                              />
                              <CardData.ColumnItem
                                 title={pageLang?.generic?.appliance_details_version || ''}
                                 value={appliance?.version}
                              />
                           </div>
                        </Card>
                     )}
                  </div>
                  <PostcodeLookup
                     errors={form?.errors}
                     data={form?.store}
                     setData={form?.setValues}
                  />
               </>
            )}
         </>
      </Stepper.Step>
   );
}

export default Step2;
